import { useEffect, useRef, useState } from "react";
import { Loader2, Send, Tent, Save, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const overline = "hr-overline text-blaze text-[11px]";
const inputCls = "w-full bg-white/5 border border-white/15 rounded px-3.5 py-2.5 outline-none focus:border-blaze text-bone text-sm placeholder:text-bone/30";

export function HuntCamp({ huntId }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [notes, setNotes] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);
  const [remoteNotes, setRemoteNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const endRef = useRef(null);

  const load = () => api.huntCamp(huntId).then((d) => {
    setData(d);
    setRemoteNotes(d.camp_notes || "");
    // Only overwrite the local textarea from the server when the user has no unsaved edits.
    setNotes((prev) => (notesDirty ? prev : (d.camp_notes || "")));
  }).catch(() => setData({ messages: [], camp_notes: "" }));
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [huntId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  const send = async () => {
    if (!text.trim()) return;
    const { message } = await api.postCampMessage(huntId, text.trim());
    setData((d) => ({ ...d, messages: [...(d?.messages || []), message] }));
    setText("");
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try { await api.updateCampNotes(huntId, notes); setRemoteNotes(notes); setNotesDirty(false); toast.success("Camp notes saved."); } catch { toast.error("Couldn't save."); }
    setSavingNotes(false);
  };

  if (!data) return <div className="py-8 grid place-items-center"><Loader2 className="animate-spin text-blaze" /></div>;

  return (
    <div className="space-y-6" data-testid="workspace-camp">
      {/* Chat */}
      <div>
        <div className="flex items-center gap-2"><MessageSquare size={15} className="text-blaze" /><span className={overline}>Party chat</span></div>
        <div className="mt-3 bg-black/20 border border-white/10 rounded-lg p-3 h-64 overflow-auto space-y-3" data-testid="camp-messages">
          {data.messages.length === 0 && <p className="text-bone/30 text-sm text-center py-8">No messages yet. Say hello to your party.</p>}
          {data.messages.map((m) => {
            const mine = m.user_id === data.me;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`camp-msg-${m.id}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${mine ? "bg-blaze text-white" : "bg-white/8 text-bone"}`}>
                  {!mine && <p className="text-[11px] text-bone/50 mb-0.5">{m.author}</p>}
                  <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <div className="flex gap-2 mt-3">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Message your party… (use @name to notify)" className={inputCls} data-testid="camp-input" />
          <button onClick={send} className="bg-blaze text-white rounded px-4 hover:bg-blaze-light transition-colors" data-testid="camp-send"><Send size={16} /></button>
        </div>
      </div>

      {/* Camp / travel notes */}
      <div className="pt-5 border-t border-white/10">
        <div className="flex items-center gap-2"><Tent size={15} className="text-blaze" /><span className={overline}>Camp & travel notes</span></div>
        <p className="text-bone/40 text-xs mt-2">Shared with your party — lodging, meet-up, transport, camp plan. Not the place for exact honey-hole coordinates.</p>
        {notesDirty && remoteNotes !== notes && remoteNotes !== "" && (
          <div className="mt-3 flex items-center justify-between gap-3 bg-blaze/10 border border-blaze/30 rounded px-3 py-2" data-testid="camp-notes-remote-changed">
            <span className="text-bone/70 text-xs">A party member updated the shared notes. Saving will overwrite theirs.</span>
            <button onClick={() => { setNotes(remoteNotes); setNotesDirty(false); }} className="text-blaze text-xs font-semibold shrink-0" data-testid="camp-notes-load-latest">Load latest</button>
          </div>
        )}
        <textarea rows={5} value={notes} onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }} placeholder="e.g. Meet at the trailhead lot 5am Fri; camp at FS road 200 pullout; Jake brings the wall tent." className={`${inputCls} mt-3`} data-testid="camp-notes" />
        <button onClick={saveNotes} disabled={savingNotes} className="mt-3 inline-flex items-center gap-2 bg-white/10 hover:bg-blaze hover:text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors disabled:opacity-50" data-testid="camp-notes-save">{savingNotes ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save notes</button>
      </div>
      <p className="text-bone/40 text-xs">Your shared gear packing list lives in the <span className="text-bone/70">Prepare</span> tab and is visible to the whole party.</p>
    </div>
  );
}
